import React, { useEffect, useState } from 'react';
import { arrayOf, func, objectOf, shape, string } from 'prop-types';
import { Box, Tab, Tabs, Typography } from '@mui/material';

import { InlineLinks, SignalSelection, SignalSearch, TabPanel } from '../../../../../../components';
import { TabsNav } from '../../../../../../global-styles/components';
import { LabelCounter } from '../../../../../../components/SignalSelection/styles';
import { getSelectedSignalCount } from '../../../Webhooks/components/WebhookForm/util';
import { getChipContent, getInitialVehicleAccessSelections } from './util';

import staticText from '../../../../../../localization/Application/Configuration/vehicleAccess';

const VehicleAccess = ({
  actions: {
    fetchSignalsRequest,
    initializeSelectedVehicleAccess,
    setSelectedVehicleAccess,
  },
  publishedVehicleAccess,
  selectedVehicleAccess,
  signals,
}) => {
  const [currentTab, setCurrentTab] = useState(0);
  const [searchActive, setSearchActive] = useState(false);

  useEffect(() => {
    fetchSignalsRequest();
  }, []);

  useEffect(() => {
    // seed selectedVehicleAccess with publishedVehicleAccess on intialization
    if (
      publishedVehicleAccess
        && Object.keys(signals.signalsByGroup).length > 0
        && Object.keys(selectedVehicleAccess).length === 0
    ) {
      const publishedVehicleAccessSelections = getInitialVehicleAccessSelections(
        publishedVehicleAccess,
        signals,
      );

      initializeSelectedVehicleAccess(publishedVehicleAccessSelections);
    }
  }, [signals.signalsByGroup, publishedVehicleAccess, selectedVehicleAccess]);


  const { signalsByGroup } = signals;
  const { signalsList, attributesList } = Object.keys(signalsByGroup).reduce(
    (acc, groupName) => {
      // For each group, add type = 'signals' to the filtered group, and type = 'attribute'
      // to the attributes list
      const filteredSignalsGroup = [];
      const filteredAttributesGroup = [];
      signalsByGroup[groupName].forEach((item) => {
        if (item.type === 'attribute') {
          filteredAttributesGroup.push(item);
        } else if (item.type === 'signal') {
          filteredSignalsGroup.push(item);
        }
      });

      if (filteredSignalsGroup.length > 0) {
        acc.signalsList[groupName] = filteredSignalsGroup;
      }
      if (filteredAttributesGroup.length > 0) {
        acc.attributesList[groupName] = filteredAttributesGroup;
      }

      return acc;
    }, { signalsList: {}, attributesList: {} },
  );

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  const selectedSignalsSet = new Set(selectedVehicleAccess.signals);
  const selectedCommandsSet = new Set(selectedVehicleAccess.commands);
  const selectedAttributesSet = new Set(selectedVehicleAccess.attributes);

  const handleSignalClick = (signalKey) => {
    setSelectedVehicleAccess('signals', signalKey);
  };
  const handleAttributesClick = (attributesKey) => {
    setSelectedVehicleAccess('attributes', attributesKey);
  };
  const handleCommandsClick = (commandsKey) => {
    setSelectedVehicleAccess('commands', commandsKey);
  };

  const selectionAndListMap = {
    signals: {
      selectionSet: selectedSignalsSet,
      list: signalsList,
    },
    commands: {
      selectionSet: selectedCommandsSet,
      list: staticText.commands.commandsList,
    },
    attributes: {
      selectionSet: selectedAttributesSet,
      list: attributesList,
    },
  };

  return (
    <Box display="flex" flexDirection="column" width="100%">
      <TabsNav color="transparent" component="nav" elevation={0}>
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          indicatorColor="primary"
        >
          {staticText.tabLabels.map((label) => {
            const { selectionSet, list } = selectionAndListMap[label.toLowerCase()];
            return (
              <Tab
                key={label}
                label={selectionSet && selectionSet.size > 0 ? (
                  <span>
                    {label}
                    <LabelCounter>
                      {getSelectedSignalCount(list, selectionSet)}
                    </LabelCounter>
                  </span>
                ) : label}
              />
            );
          })}
        </Tabs>
      </TabsNav>
      <Box flex={1} overflow="hidden">
        <TabPanel
          value={currentTab}
          index={0}
          pageName={staticText.tabLabels[0]}
        >
          {Object.keys(signalsList).length > 0 && (
            <React.Fragment>
              <SignalSearch
                signals={signalsList}
                selectedSignals={selectedSignalsSet}
                setSearchActive={setSearchActive}
                handleSignalClick={handleSignalClick}
              />
              {!searchActive && (
                <React.Fragment>
                  <Typography sx={{ mt: 3, mb: 3 }} variant="body2">
                    {staticText.signals.description}
                  </Typography>
                  <SignalSelection
                    signals={signalsList}
                    selectedSignals={selectedSignalsSet}
                    handleSignalClick={handleSignalClick}
                    showSelectedSignalsOnly={false}
                    enableSignalGroupSelection={false}
                    getChipContent={getChipContent}
                    columnTitles={staticText.signals.selectionColumnLabels}
                  />
                </React.Fragment>
              )}
            </React.Fragment>
          )}
        </TabPanel>
        <TabPanel
          value={currentTab}
          index={1}
          pageName={staticText.tabLabels[1]}
        >
          {Object.keys(staticText.commands.commandsList).length > 0 && (
            <React.Fragment>
              <Typography sx={{ mt: 3, mb: 3 }} variant="body2">
                <InlineLinks
                  text={staticText.commands.description}
                />
              </Typography>
              <SignalSelection
                signals={staticText.commands.commandsList}
                selectedSignals={selectedCommandsSet}
                handleSignalClick={handleCommandsClick}
                showSelectedSignalsOnly={false}
                enableSignalGroupSelection={false}
                getChipContent={getChipContent}
                columnTitles={staticText.commands.selectionColumnLabels}
              />
            </React.Fragment>
          )}

        </TabPanel>
        <TabPanel
          value={currentTab}
          index={2}
          pageName={staticText.tabLabels[2]}
        >
          {Object.keys(attributesList).length > 0 && (
            <React.Fragment>
              <Typography sx={{ mt: 3, mb: 3 }} variant="body2">
                {staticText.attributes.description}
              </Typography>
              <SignalSelection
                signals={attributesList}
                selectedSignals={selectedAttributesSet}
                handleSignalClick={handleAttributesClick}
                showSelectedSignalsOnly={false}
                enableSignalGroupSelection={false}
                getChipContent={getChipContent}
                columnTitles={staticText.attributes.selectionColumnLabels}
              />
            </React.Fragment>
          )}
        </TabPanel>
      </Box>
    </Box>
  );
};

export default VehicleAccess;

VehicleAccess.propTypes = {
  actions: shape({
    fetchSignalsRequest: func.isRequired,
    initializeSelectedVehicleAccess: func.isRequired,
    setSelectedVehicleAccess: func.isRequired,
  }).isRequired,
  publishedVehicleAccess: arrayOf(string).isRequired,
  selectedVehicleAccess: shape({
    signals: arrayOf(string),
    attributes: arrayOf(string),
    commands: arrayOf(string),
  }).isRequired,
  signals: objectOf(
    shape({
      signalsByGroup: arrayOf(
        shape({
          name: string.isRequired,
          description: string.isRequired,
          type: string.isRequired,
          permission: string.isRequired,
        }),
      ),
    }),
  ).isRequired,
};

